import { Injectable } from '@angular/core';
import { combineLatest, take } from 'rxjs';

import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade } from '@mode/charts/contract';
import { ReportEditorFacade } from '@mode/report-editor/domain';
import { ReportFacade } from '@mode/reports/domain';
import { DatasetBrowserData, DataViewTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { SwitchViewFacade } from '@mode/switch-views/contract';

@Injectable()
export class DatasetSearchImportModalComponentFacade {
  constructor(
    private accountFacade: AccountFacade,
    private chartFacade: ChartFacade,
    private reportFacade: ReportFacade,
    private reportEditorFacade: ReportEditorFacade,
    private switchViewFacade: SwitchViewFacade
  ) {}

  public importDataset(dataset: DatasetBrowserData, isSelectedDataset: boolean) {
    combineLatest([this.accountFacade.defaultCategoricalPalette$, this.accountFacade.currentOrg$])
      .pipe(take(1))
      .subscribe(([palette, org]) => {
        const chartData = this.createChartEncodingAndFormats(dataset);
        const accountId = org.username;
        if (isSelectedDataset) {
          this.handleImportFromSearch(accountId, dataset, palette.token, chartData);
        } else {
          this.handleImportFromUrl(accountId, dataset, palette.token, chartData);
        }
      });
  }

  public importDatasetExistingReport(
    dataset: DatasetBrowserData,
    isSelectedDataset: boolean,
    queryToken: string,
    reportId: string
  ) {
    combineLatest([this.accountFacade.defaultCategoricalPalette$, this.accountFacade.currentOrg$])
      .pipe(take(1))
      .subscribe(([palette, org]) => {
        const chartData = this.createChartEncodingAndFormats(dataset);
        const accountId = org.username;
        if (isSelectedDataset) {
          this.handleImportFromSearchExistingReport(accountId, reportId, palette.token, chartData, queryToken);
        } else {
          this.handleImportFromUrlExistingReport(accountId, reportId, palette.token, chartData, queryToken);
        }
      });
  }

  private handleImportFromSearchExistingReport(
    accountId: string,
    reportId: string,
    paletteToken: string,
    chartData: any,
    queryToken: string
  ) {
    this.reportEditorFacade.importDatasetWithChartFromSearch(
      accountId,
      reportId,
      queryToken,
      paletteToken,
      chartData.encWithColumns?.encoding,
      chartData.chartFormats.format,
      chartData.chartFormats.fieldsFormat
    );
  }

  private handleImportFromUrlExistingReport(
    accountId: string,
    reportId: string,
    paletteToken: string,
    chartData: any,
    queryToken: string
  ) {
    this.reportEditorFacade.importDatasetWithChartFromUrl(
      accountId,
      reportId,
      queryToken,
      paletteToken,
      chartData.encWithColumns?.encoding,
      chartData.chartFormats.format,
      chartData.chartFormats.fieldsFormat
    );
  }

  private handleImportFromSearch(accountId: string, dataset: DatasetBrowserData, paletteToken: string, chartData: any) {
    this.reportFacade.createReportAndChartFromDatasetBrowserSearch(
      accountId,
      dataset.switchViewToken,
      paletteToken,
      chartData.encWithColumns?.encoding,
      chartData.chartFormats.format,
      chartData.chartFormats.fieldsFormat
    );
  }

  private handleImportFromUrl(accountId: string, dataset: DatasetBrowserData, paletteToken: string, chartData: any) {
    this.reportFacade.createReportAndChartFromDatasetBrowserUrl(
      accountId,
      dataset.switchViewToken,
      paletteToken,
      chartData.encWithColumns?.encoding,
      chartData.chartFormats.format,
      chartData.chartFormats.fieldsFormat
    );
  }

  private createChartEncodingAndFormats(dataset: DatasetBrowserData) {
    const switchAttrs = dataset.switchAttrs ?? [];
    const attrs: FlamingoTypes.ModeAttr[] = this.switchViewFacade.makeAttrs(switchAttrs);
    const switchView = this.switchViewFacade.createSwitchView(
      <string>dataset.queryToken,
      <string>dataset.queryRunToken,
      <DataViewTypes.DatasetResourceColumn[]>dataset.metaColumns,
      attrs
    );
    const enc = this.chartFacade.createTableChartEncoding();
    const model = this.switchViewFacade.makeView(switchView);
    const columns = this.chartFacade.createTableColumns(model);

    return {
      encWithColumns: this.chartFacade.addRemainingColumnsToTable(enc, model, <string>dataset?.queryToken),
      chartFormats: this.chartFacade.createTableChartFormats(columns),
    };
  }
}
